import * as React from 'react';
import Avatar from 'antd/lib/avatar';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { codeIsCommit } from '../../helpers';
import { CommitItem, FeatureItem, PRItem } from '../../types';
import { antGrey, getAvatarColorForName } from '../styles/globalStyles';

const AVATAR_SIZE = 20;
const AVATAR_OFFSET = 12;
const MAX_STACKED_AVATARS = 2;

interface GroupedPeopleProps {
    names: string[];
    // singular, e.g. "owner" or "author"
    noun: string;
}

// Owner / author column for a grouped row: up to two stacked avatars and "N <noun>s", with the full
// comma-separated list on hover
const GroupedPeople: React.FC<GroupedPeopleProps> = ({ names, noun }) => {
    const distinctNames = Array.from(new Set(names.filter((name) => !!name)));

    if (distinctNames.length === 0) {
        return <Text style={{ color: antGrey.grey5 }}>No {noun}s</Text>;
    }

    const stacked = distinctNames.slice(0, MAX_STACKED_AVATARS);
    const label = `${distinctNames.length} ${noun}${distinctNames.length === 1 ? '' : 's'}`;

    return (
        <Tooltip title={distinctNames.join(', ')} placement="right">
            {/* inline so the tooltip anchors to the content, not the full-width people column */}
            <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                <div
                    style={{
                        position: 'relative',
                        width: AVATAR_SIZE + AVATAR_OFFSET * (stacked.length - 1),
                        height: AVATAR_SIZE,
                        marginRight: 8,
                        flexShrink: 0,
                    }}
                >
                    {stacked.map((name, i) => (
                        <Avatar
                            key={name}
                            size={AVATAR_SIZE}
                            style={{
                                fontSize: 10,
                                backgroundColor: getAvatarColorForName(name),
                                position: 'absolute',
                                left: i * AVATAR_OFFSET,
                                zIndex: stacked.length - i,
                                // outside the circle, so every avatar keeps the same size
                                boxShadow: i > 0 ? '0 0 0 1.5px white' : undefined,
                            }}
                        >
                            {name.charAt(0).toUpperCase()}
                        </Avatar>
                    ))}
                </div>
                <Text style={{ color: antGrey.grey8, whiteSpace: 'nowrap' }}>{label}</Text>
            </div>
        </Tooltip>
    );
};

export default GroupedPeople;

// Short label for a code item: PR number when it is or came from a PR, otherwise the short commit hash
export const shortLabelForCodeItem = (code: CommitItem | PRItem) =>
    !codeIsCommit(code) ? `#${code.identifier}`
    : code.sourcePR ? `#${code.sourcePR.number}`
    : code.identifier.substring(0, 7);

export const shortLabelForTicketItem = (item: FeatureItem) =>
    item.ticket?.displayableIdentifier ?? item.ticket?.identifier ?? item.id;
